import { SplashScreen } from '@capacitor/splash-screen';
import { register } from 'swiper/element/bundle';
import { UserAuthService } from './services/user-auth.service';
import { LanguageService } from './services/language.service';
import { setCssVariables } from 'src/theme-css-variables';
import { Platform } from '@ionic/angular';
import { smallScreenSize, generateString, presentSnackBar, updateObjKeepingRef } from 'src/global';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PagesEnum } from 'node_js/models/enums/pages';
import { LiveUpdatesService } from './services/live-updates.service';
import { UserNotification } from 'node_js/models/tables/user_notification';
import { UserContact } from 'node_js/models/tables/user_contact';
import { UserContactStatus } from 'node_js/models/enums/user-contact-status';
import { LabService } from './services/lab.service';
import { LabUsersUpdate } from 'node_js/models/business/lab/lab-users-update';
import { ComponentSettingsReturn } from 'node_js/models/business/component-settings-return';
import { Component } from 'node_js/models/tables/component';
import { Component as AngularComponent, NgZone, OnInit, OnDestroy } from '@angular/core';
import { ComponentSchedule } from 'node_js/models/tables/component_schedule';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { GadgetService } from './services/gadget.service';
import { GadgetUser } from 'node_js/models/tables/gadget_user';
import { GadgetUserComplete } from 'node_js/models/business/lab/gadget-user-complete';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { LabUserComplete } from 'node_js/models/business/lab/lab-user-complete';
import { LabUserCombo } from 'node_js/models/business/lab/lab-user-combo';
import { GadgetUsersUpdate } from 'node_js/models/business/lab/gadget-users-update';
import { ioLabSettings } from 'node_js/models/business/lab/io-lab-settings';
import { ioGadgetSettings } from 'node_js/models/business/lab/io-gadget-settings';
import { LabControllerComplete } from 'node_js/models/business/lab/lab-controller-complete';
import { UserNotificationService } from './services/user-notification.service';
import { RoleEnum } from 'node_js/models/enums/role';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { PushNotificationService } from './services/push-notification.service';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { App } from '@capacitor/app';
import { filter } from 'rxjs';
import { IoRoomHeaderEnum } from 'node_js/models/enums/io-room-header';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { TutorialService } from './services/tutorial.service';
import { LabUserListComponent } from './components/lab-user-list/lab-user-list.component';
import { GadgetUserListComponent } from './components/gadget-user-list/gadget-user-list.component';
import { StateService } from './services/state.service';
import { UiDomTranslationService } from './services/ui-dom-translation.service';
register();

const DASHBOARD_LAB_SLIDE = 5;
const DASHBOARD_GADGET_SLIDE = 6;
const DASHBOARD_CONTROLLER_SLIDE = 8;

@AngularComponent({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  // --- Splash properties ---
  showSplash = false; // Start with HTML splash hidden
  splashFadeOut = false;
  splashImageSrc = 'assets/backgrounds/splash.svg';
  splashImageSrcMobile = 'assets/backgrounds/splash-mobile.svg';

  public testingSplash = false; // Set to true to keep splash always visible for testing
  // Add any other splash-related properties you need here

  // --- Original app logic properties ---
  minimumSplashScreenTime = 5000;
  finishedMinimumSplashScreenTime = false;
  disableAfterMinimumTime = false;
  loadedSplashScreen = false;
  handledSplashScreenFunctionality = false;

  // Other app properties
  hideApp: boolean = true;
  public welcomePageReady = false;
  public hideLogoContainer = false;
  pageTransitioning = false;

  //Flag to detect if we've hit the max splash time and are waiting on login status
  waitingOnUserLoginCheckToFinishIntro = false;

  // Original properties
  firstLoad = true; // Track if it's first app boot
  onWelcomePage = false;
  hasReachedMinimumTime = false;

  firstAppLoad = true;
  imageLoaded = false;
  splashSoundSrc = 'assets/sounds/splash.wav';

  protected window = window;
  protected smallScreenSize = smallScreenSize;

  // Add this property to the class
  isResumeSplash: boolean = false;
  private socketEventListenersSetup = false;
  splashFallbackTimeoutId: any = null;
  splashFadeOutTriggered: boolean = false; // Add this flag to prevent multiple fade outs

  // Subscription management for socket events
  private socketSubscriptions: Subscription = new Subscription();
  private lastSilentRefreshTime = 0;
  private isRefreshing = false;
  private backgroundedAt = 0;

  /**
   * Emits when the app has reloaded user data and reconnected sockets after resume.
   * ReplaySubject(1) so late subscribers (e.g. dashboard mounted after emission) still get it.
   */
  public reloadedApp$ = new ReplaySubject<void>(1);

  tutorialShadeStyles: Record<string, string>[] = [];
  tutorialShadePath = '';
  tutorialShadeFill = 'rgba(5, 8, 18, 0.7)';
  tutorialSpotlightStyle: Record<string, string> = {};
  tutorialSpotlightCutoutStyle: Record<string, string> = {};
  tutorialHasTarget = false;
  tutorialSheetAtTop = false;
  private tutorialOverlayIntervalId: any = null;
  private tutorialAutoScrolledTargetSelector: string | null = null;

  constructor(
    private userAuthService: UserAuthService, private matSnackBar: MatSnackBar, private platform: Platform,
    private liveUpdatesService: LiveUpdatesService, private labService: LabService, private gadgetService: GadgetService,
    private userNotificationService: UserNotificationService, private pushNotificationService: PushNotificationService,
    private router: Router, private ngZone: NgZone, public tutorialService: TutorialService,
    private matDialog: MatDialog, private stateService: StateService, private uiDomTranslationService: UiDomTranslationService
  ) {

    if (Capacitor.getPlatform() == "web") {
      this.minimumSplashScreenTime = 1500;
    }

    // --- Splash logic (keep this as is) ---
    this.startSplashWithOptionalDelay(this.minimumSplashScreenTime);
    setCssVariables();



    // Start auth check after splash screen has begun
    this.userAuthService.startAuthCheck();

    let userAuthServiceObs = this.userAuthService.fAuthStateChangedObservable.subscribe(async () => {
      if (this.userAuthService.didLoginCheck) {
        if (this.handledSplashScreenFunctionality == false) {
          console.log("SHOULD ONLY BE CALLED ONCE")
          this.handledSplashScreenFunctionality = true;
          if (this.finishedMinimumSplashScreenTime) {
            if (this.firstAppLoad) {
              console.log("First app load, hiding splash screen");
              this.finishIntro();
            }

          }
          else {
            //this.finishIntro();
            console.log("disable after  min is true")
            this.disableAfterMinimumTime = true;
          }
        }

        //HANDLE LIVE UPDATES
        if (this.userAuthService.loggedIn) {
          // Set up socket connection in background, don't block splash screen
          await this.setupSocketConnection();

          for (let i = 0; i < this.userAuthService.currentUserComplete.userNotificationsComplete.userNotifications.length; i++) {
            let userNotification = this.userAuthService.currentUserComplete.userNotificationsComplete.userNotifications[i];
            await this.userNotificationService.setupUserNotificationLocally(userNotification);
          }
          // Room joining is now handled inside setupSocketConnection after event listeners are ready
        }
        else {
          // User logged out - disconnect and reset all state
          console.log('[App] User logged out, cleaning up all state');
          this.liveUpdatesService.disconnect();
          this.socketEventListenersSetup = false;

          // Clear socket subscriptions
          if (this.socketSubscriptions) {
            this.socketSubscriptions.unsubscribe();
            this.socketSubscriptions = new Subscription();
          }

          // Ensure lab service is completely cleared
          this.labService.clearAllData();

          console.log('[App] User logout cleanup completed');
        }
      }
    })




    App.addListener('appStateChange', async (stateData: { isActive: boolean }) => {
      if (!stateData.isActive) {
        this.backgroundedAt = Date.now();
        console.log('[App] Backgrounded');
      } else {
        console.log('[App] Foregrounded');
        if (this.backgroundedAt > 0) { // Guard against spurious isActive:true on launch
          await this.appStateChanged();
        }
        this.backgroundedAt = 0;
      }
    });

    // Web browser tab visibility — covers Android/iOS browsers where App events don't fire
    if (!Capacitor.isNativePlatform()) {
      document.addEventListener('visibilitychange', async () => {
        if (document.visibilityState === 'visible' && this.userAuthService.loggedIn) {
          if (!this.liveUpdatesService.isConnected()) {
            window.location.reload();
          } else {
            await this.silentRefresh();
          }
        }
      });
    }

    // Socket auto-reconnect — fires when Socket.IO recovers on its own (e.g. brief network blip)
    this.liveUpdatesService.reconnected$.subscribe(() => {
      void this.silentRefresh();
    });

    if (this.isMobileDevice()) {
      this.setupPushNotifications();
    }

    // Set up deep link handling for OAuth redirects
    // this.setupDeepLinkHandling(); // No longer needed with Google SDK
  }

  private async setupSocketConnection() {
    console.log('[App] Setting up socket connection in background...');

    try {
      await this.liveUpdatesService.connect(this.userAuthService);

      // Set up a one-time listener for when socket connects to ensure listeners are set up
      if (this.liveUpdatesService.socket) {
        this.liveUpdatesService.socket.on('connect', () => {
          console.log('[App] Socket connected event received, setting up event listeners and joining rooms');
          // Reset flag on new connection to ensure listeners are set up (socket instance may be new)
          this.socketEventListenersSetup = false;
          this.setupSocketEventListenersAndJoinRooms();
        });

        // If already connected, set up listeners immediately
        if (this.liveUpdatesService.isConnected()) {
          console.log('[App] Socket already connected, setting up event listeners and joining rooms');
          this.socketEventListenersSetup = false;
          this.setupSocketEventListenersAndJoinRooms();
        }
      } else {
        console.warn('[App] Socket not created, waiting...');
        // Wait a bit and try again
        setTimeout(async () => {
          if (this.liveUpdatesService.socket && this.liveUpdatesService.isConnected()) {
            console.log('[App] Socket now connected, setting up event listeners and joining rooms');
            this.socketEventListenersSetup = false;
            this.setupSocketEventListenersAndJoinRooms();
          } else {
            console.error('[App] Socket still not connected after delay');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('[App] Error setting up socket connection:', error);
    }
  }

  private setupSocketEventListenersAndJoinRooms() {
    // Only set up event listeners once to prevent duplicates
    if (!this.socketEventListenersSetup) {
      console.log('[App] Setting up socket event listeners (first time)');
      this.setupSocketEventListeners();
      this.socketEventListenersSetup = true;
    } else {
      console.log('[App] Socket event listeners already set up, skipping');
    }

    // Always join rooms when socket is connected (for reconnections)
    console.log('[App] Joining all user lab rooms');
    this.liveUpdatesService.joinAllUserLabRooms(this.userAuthService);
  }

  ngOnInit(): void {
    if (this.testingSplash) {
      this.showSplash = false;
      setTimeout(() => {
        this.showSplash = true;
      }, 100);
    }
    this.userAuthService.fAuthStateChangedObservable.subscribe(async () => {
      if (this.userAuthService.loggedIn) {
        this.setupPushNotifications();
      }
    })
    if (this.userAuthService.didLoginCheck && this.userAuthService.loggedIn) {
      this.setupPushNotifications();
    }

    this.startTutorialOverlaySync();
  }

  ngOnDestroy(): void {
    if (this.socketSubscriptions) {
      this.socketSubscriptions.unsubscribe();
    }

    if (this.tutorialOverlayIntervalId) {
      clearInterval(this.tutorialOverlayIntervalId);
      this.tutorialOverlayIntervalId = null;
    }
  }

  cancelTutorial() {
    this.tutorialService.cancelActiveTutorial();
    this.clearTutorialOverlay();
  }

  dismissTutorialCompletion() {
    this.tutorialService.dismissCompletion();
  }

  private changeDashboardSlideThenRun(targetSlide: number, callback: () => void) {
    let finished = false;
    let fallbackTimeoutId: any = null;

    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      if (fallbackTimeoutId) {
        clearTimeout(fallbackTimeoutId);
      }
      settledSubscription.unsubscribe();
      callback();
    };

    const settledSubscription = this.stateService.getMainSliderSectionSettledObservable().subscribe((settledSlide: number) => {
      if (settledSlide === targetSlide) {
        finish();
      }
    });

    fallbackTimeoutId = setTimeout(() => finish(), 600);
    this.stateService.triggerMainSliderSectionObservable(targetSlide as any, false);
  }

  continueTutorialCompletion() {
    if (!this.labService.currentSelectedLab) {
      this.tutorialService.dismissCompletion();
      return;
    }

    switch (this.tutorialService.justCompletedTutorialId) {
      case 'create-lab': {
        this.tutorialService.startLabMembersTutorial();

        const dialogRef = this.matDialog.open(LabUserListComponent, {
          height: 'auto',
          maxHeight: '600px',
          width: '370px',
          maxWidth: '93vw',
          data: { labComplete: this.labService.currentSelectedLab },
          autoFocus: false,
        });

        dialogRef.beforeClosed().subscribe(() => {
          const componentInstance = dialogRef.componentInstance;
          if (componentInstance && typeof componentInstance.cleanupBeforeClose === 'function') {
            componentInstance.cleanupBeforeClose();
          }
        });
        return;
      }
      case 'lab-members':
        this.matDialog.closeAll();
        this.changeDashboardSlideThenRun(DASHBOARD_LAB_SLIDE, () => {
          this.tutorialService.startCreateGadgetTutorial();
        });
        return;
      case 'create-gadget': {
        if (this.tutorialService.shouldResumeDashboardControllerAfterCreateGadget) {
          this.matDialog.closeAll();
          this.tutorialService.startCreateComponentTutorialFromDashboardController();
          return;
        }

        this.tutorialService.startGadgetUsersTutorial();
        const dialogRef = this.matDialog.open(GadgetUserListComponent, {
          height: 'auto',
          maxHeight: '600px',
          width: '370px',
          maxWidth: '93vw',
          data: {
            labComplete: this.labService.currentSelectedLab,
            gadgetComplete: this.labService.currentSelectedGadget,
          },
          autoFocus: false,
        });

        dialogRef.beforeClosed().subscribe(() => {
          const componentInstance = dialogRef.componentInstance;
          if (componentInstance && typeof componentInstance.cleanupBeforeClose === 'function') {
            componentInstance.cleanupBeforeClose();
          }
        });
        return;
      }
      case 'gadget-users':
        this.matDialog.closeAll();
        this.tutorialService.startCreateComponentTutorial();
        return;
      case 'create-component':
        this.matDialog.closeAll();
        this.tutorialService.startComponentGuideTutorial();
        return;
      case 'component-guide':
        this.matDialog.closeAll();
        this.changeDashboardSlideThenRun(DASHBOARD_CONTROLLER_SLIDE, () => {
          if (this.tutorialService.shouldResumeDashboardControllerAfterCreateComponent) {
            this.tutorialService.resumeDashboardControllerTutorialAfterCreateComponent();
            return;
          }

          const creatableDashboardTutorialGadgets = (this.labService.currentSelectedLab?.gadgetsComplete || []).filter(
            gadgetComplete => gadgetComplete.currentGadgetUserComplete?.gadgetUser?.roleId <= RoleEnum.CREATOR
          );

          this.tutorialService.startDashboardControllerTutorial({
            hasGadgets: creatableDashboardTutorialGadgets.length > 0,
            hasComponents: creatableDashboardTutorialGadgets.some(
              gadgetComplete => (gadgetComplete.componentsComplete?.length || 0) > 0
            ),
            canCreateGadgetInLab: (this.labService.currentSelectedLab?.currentLabUserComplete?.labUser?.roleId || Number.MAX_SAFE_INTEGER) <= RoleEnum.CREATOR,
            gadgetOptions: creatableDashboardTutorialGadgets.map(gadgetComplete => ({
              id: gadgetComplete.gadget.id,
              name: gadgetComplete.gadgetLanguage?.name || 'Unnamed Gadget',
            })),
          });
        });
        return;
      default:
        this.tutorialService.dismissCompletion();
        return;
    }
  }

  runTutorialStepAction() {
    switch (this.tutorialService.activeStepId) {
      case 'new-lab-create-intro':
        this.tutorialService.advanceCreateLabIntroToJoin();
        return;
      case 'new-lab-join-intro':
        this.tutorialService.advanceCreateLabIntroToClone();
        return;
      case 'new-lab-clone-intro':
        this.tutorialService.finishCreateLabIntro();
        return;
      case 'create-lab-name':
        this.tutorialService.advanceCreateLabNameStep();
        return;
      case 'create-gadget-name':
        this.tutorialService.advanceCreateGadgetNameStep();
        return;
      case 'create-component-name':
        this.tutorialService.advanceCreateComponentNameStep();
        return;
      case 'dashboard-controller-name':
        this.tutorialService.advanceDashboardControllerNameStep();
        return;
      case 'create-lab-upload-custom':
        this.tutorialService.advanceCreateLabToCuratedIcons();
        return;
      case 'lab-settings-edit-info':
        this.tutorialService.advanceLabSettingsToQuickJoin();
        return;
      case 'lab-settings-quick-join-code':
      case 'lab-settings-quick-join-copy':
        this.tutorialService.advanceLabSettingsQuickJoinStep();
        return;
      case 'lab-settings-quick-join-refresh':
        this.matDialog.closeAll();
        this.tutorialService.finishCreateLabSettingsGuide();
        return;
      case 'create-gadget-upload-custom':
        this.tutorialService.advanceCreateGadgetToCuratedIcons();
        return;
      case 'gadget-settings-gear':
        this.matDialog.closeAll();
        this.tutorialService.acknowledgeCreateGadgetSettings();
        return;
      case 'create-component-upload-custom':
        this.tutorialService.advanceCreateComponentToCuratedIcons();
        return;
      case 'dashboard-controller-needs-gadget':
        if (this.labService.currentSelectedLab?.currentLabUserComplete?.labUser?.roleId > RoleEnum.CREATOR) {
          presentSnackBar('You need Creator access in this lab before you can create a gadget for the dashboard tutorial.', SnackBarColor.DANGER, this.matSnackBar);
          return;
        }

        this.matDialog.closeAll();
        this.changeDashboardSlideThenRun(DASHBOARD_LAB_SLIDE, () => {
          this.tutorialService.startCreateGadgetTutorialFromDashboardController();
        });
        return;
      case 'dashboard-controller-needs-component': {
        const selectedGadgetId = this.tutorialService.dashboardControllerSelectedGadgetId;
        const creatableGadget = (this.labService.currentSelectedLab?.gadgetsComplete || []).find(
          gadgetComplete => gadgetComplete.gadget.id === selectedGadgetId
            && gadgetComplete.currentGadgetUserComplete?.gadgetUser?.roleId <= RoleEnum.CREATOR
        ) || null;

        if (!creatableGadget) {
          presentSnackBar('You need a gadget you can edit before you can create a component for the dashboard tutorial.', SnackBarColor.DANGER, this.matSnackBar);
          return;
        }

        this.matDialog.closeAll();
        this.labService.currentSelectedGadget = creatableGadget;
        this.changeDashboardSlideThenRun(DASHBOARD_GADGET_SLIDE, () => {
          this.tutorialService.startCreateComponentTutorialFromDashboardController();
        });
        return;
      }
      case 'dashboard-controller-connect-text':
      case 'dashboard-controller-connect-button':
      case 'dashboard-controller-connect-graph':
      case 'dashboard-controller-connect-analog':
      case 'dashboard-controller-connect-slider':
      case 'dashboard-controller-connect-toggle':
      case 'dashboard-controller-connect-gauge':
      case 'dashboard-controller-connect-warning':
      case 'dashboard-controller-connect-message':
        this.tutorialService.advanceDashboardControllerConnectedInputStep();
        return;
      case 'dashboard-controller-button-mode':
      case 'dashboard-controller-button-values':
      case 'dashboard-controller-analog-values':
        this.tutorialService.advanceDashboardControllerConfiguredInputStep();
        return;
      case 'dashboard-controller-try-button':
        this.tutorialService.advanceDashboardControllerPracticeStep();
        return;
      case 'dashboard-controller-quick-mode':
        this.tutorialService.completeDashboardControllerTutorial();
        return;
      case 'component-guide-data-type':
      case 'component-guide-current-value':
      case 'component-guide-mqtt-topic':
      case 'component-guide-value-triggers':
      case 'component-guide-value-trigger-condition':
      case 'component-guide-value-trigger-effect-type':
      case 'component-guide-value-trigger-effect-change-value':
      case 'component-guide-value-trigger-effect-push':
      case 'component-guide-schedules':
      case 'component-guide-schedule-frequency':
      case 'component-guide-schedule-next-date':
      case 'component-guide-schedule-weeks':
      case 'component-guide-schedule-months':
      case 'component-guide-schedule-effects':
        this.tutorialService.advanceComponentGuide();
        return;
      case 'contact-info-nickname':
        this.tutorialService.advanceAddContactNicknameStep();
        return;
      case 'contact-info-method':
        this.tutorialService.advanceAddContactMethodStep();
        return;
      case 'contact-info-email':
        this.tutorialService.advanceAddContactEmailStep();
        return;
      case 'contact-info-code':
        this.tutorialService.advanceAddContactCodeStep();
        return;
      case 'contact-info-finish':
        this.tutorialService.advanceAddContactFinishStep();
        return;
      case 'user-settings-friend-code':
        this.tutorialService.completeAddContactTutorial();
        return;
      default:
        return;
    }
  }

  private startTutorialOverlaySync() {
    this.syncTutorialOverlay();

    this.tutorialOverlayIntervalId = setInterval(() => {
      this.syncTutorialOverlay();
    }, 150);
  }

  private syncTutorialOverlay() {
    if (!this.tutorialService.isTutorialActive) {
      this.clearTutorialOverlay();
      return;
    }

    if (this.tutorialService.isOverlayPaused) {
      this.setTutorialShadeToFullscreen();
      return;
    }

    const targetSelector = this.tutorialService.activeTargetSelector;

    if (!targetSelector) {
      this.setTutorialShadeToFullscreen();
      return;
    }

    const targetElement = document.querySelector(targetSelector) as HTMLElement | null;

    if (!targetElement) {
      this.setTutorialShadeToFullscreen();
      return;
    }

    const tutorialTargetElements = this.getTutorialTargetElements(targetElement);
    const primaryTutorialTargetElement = tutorialTargetElements[0] || targetElement;
    const shouldAutoScrollTarget = this.shouldScrollTutorialTargetIntoView(primaryTutorialTargetElement);

    if (shouldAutoScrollTarget) {
      this.tryScrollTutorialTargetIntoView(targetSelector, primaryTutorialTargetElement);
      if (this.shouldHideTutorialHighlightDuringAutoScroll(primaryTutorialTargetElement)) {
        this.setTutorialShadeToFullscreen();
        return;
      }
    }

    const rect = this.getCombinedTutorialTargetRect(tutorialTargetElements);

    if (!rect || !rect.width || !rect.height) {
      if (this.tryScrollTutorialTargetIntoView(targetSelector, targetElement)) {
        this.setTutorialShadeToFullscreen();
        return;
      }

      this.setTutorialShadeToFullscreen();
      return;
    }

    if (!this.shouldScrollTutorialTargetIntoView(primaryTutorialTargetElement)) {
      this.tutorialAutoScrolledTargetSelector = null;
    }

    const padding = this.getTutorialTargetPadding(tutorialTargetElements[0]);
    const margin = 8;
    const targetBorderRadius = this.tutorialService.activeStepDetails?.targetBorderRadius || this.getTutorialTargetBorderRadius(tutorialTargetElements[0]);
    const top = Math.max(margin, rect.top - padding);
    const left = Math.max(margin, rect.left - padding);
    const right = Math.min(window.innerWidth - margin, rect.right + padding);
    const bottom = Math.min(window.innerHeight - margin, rect.bottom + padding);
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);

    this.tutorialHasTarget = true;
    this.tutorialSpotlightStyle = {
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
      'border-radius': targetBorderRadius,
    };

    const shadeBackground = this.tutorialService.activeStepDetails?.showShade === false
      ? 'transparent'
      : 'rgba(5, 8, 18, 0.7)';
    const targetBorderRadiusValues = this.getTutorialTargetBorderRadiusValues(targetBorderRadius, width, height);

    this.tutorialShadeFill = shadeBackground;
    this.tutorialShadePath = this.buildTutorialShadePath(left, top, width, height, targetBorderRadiusValues);
    this.tutorialSpotlightCutoutStyle = {};
    this.tutorialShadeStyles = [];

    this.tutorialSheetAtTop = this.shouldPlaceTutorialSheetAtTop(bottom);
  }

  private shouldPlaceTutorialSheetAtTop(targetBottom: number | null): boolean {
    if (this.shouldPinComponentGuideSheetToTopOnMobile()) {
      return true;
    }

    if (this.shouldPinDashboardControllerSheetToTopOnMobile()) {
      return true;
    }

    if (this.shouldPinComponentGuideSheetToTopOnDesktop()) {
      return true;
    }

    if (this.shouldPinComponentGuideSheetToBottomOnDesktop()) {
      return false;
    }

    switch (this.tutorialService.activeSheetPosition) {
      case 'top':
        return true;
      case 'bottom':
        return false;
      default:
        return targetBottom !== null ? targetBottom > (window.innerHeight - 240) : false;
    }
  }

  private shouldPinComponentGuideSheetToTopOnMobile(): boolean {
    if (window.innerWidth > 768) {
      return false;
    }

    switch (this.tutorialService.activeStepId) {
      case 'component-guide-data-type':
      case 'component-guide-current-value':
      case 'component-guide-mqtt-topic':
      case 'component-guide-schedules':
      case 'component-guide-schedule-add':
      case 'component-guide-schedule-frequency':
      case 'component-guide-schedule-next-date':
      case 'component-guide-schedule-weeks':
      case 'component-guide-schedule-months':
      case 'component-guide-schedule-effects':
        return true;
      default:
        return false;
    }
  }

  private shouldPinDashboardControllerSheetToTopOnMobile(): boolean {
    if (window.innerWidth > 768) {
      return false;
    }

    switch (this.tutorialService.activeStepId) {
      case 'dashboard-controller-select-button':
        return true;
      default:
        return false;
    }
  }

  private shouldPinComponentGuideSheetToTopOnDesktop(): boolean {
    if (window.innerWidth <= 768) {
      return false;
    }

    switch (this.tutorialService.activeStepId) {
      case 'component-guide-data-type':
      case 'component-guide-current-value':
      case 'component-guide-mqtt-topic':
      case 'component-guide-value-trigger-effect-change-value':
      case 'component-guide-value-trigger-effect-push':
      case 'component-guide-finish':
        return true;
      default:
        return false;
    }
  }

  private shouldPinComponentGuideSheetToBottomOnDesktop(): boolean {
    if (window.innerWidth <= 768 || !this.tutorialService.isComponentGuideTutorialActive) {
      return false;
    }

    switch (this.tutorialService.activeStepId) {
      case 'component-guide-data-type':
      case 'component-guide-current-value':
      case 'component-guide-mqtt-topic':
      case 'component-guide-value-trigger-effect-change-value':
      case 'component-guide-value-trigger-effect-push':
      case 'component-guide-finish':
        return false;
      default:
        return true;
    }
  }

  private getTutorialTargetElements(targetElement: HTMLElement): HTMLElement[] {
    const spotlightTargetElement = this.getTutorialSpotlightTargetElement(targetElement);
    const targetElements = [spotlightTargetElement];
    const expandedSelectTrigger = this.getExpandedTutorialSelectTrigger(spotlightTargetElement)
      || this.getExpandedTutorialSelectTrigger(targetElement);
    const selectPanelId = expandedSelectTrigger?.getAttribute('aria-controls') || expandedSelectTrigger?.getAttribute('aria-owns');
    const selectPanelElement = selectPanelId ? document.getElementById(selectPanelId) as HTMLElement | null : null;

    if (selectPanelElement) {
      targetElements.push(selectPanelElement);
    }

    return targetElements;
  }

  private getTutorialSpotlightTargetElement(targetElement: HTMLElement): HTMLElement {
    const dialogSurfaceElement = this.getTutorialDialogSurfaceElement(targetElement);
    if (dialogSurfaceElement) {
      return dialogSurfaceElement;
    }

    if (targetElement.hasAttribute('data-tutorial-spotlight-self')) {
      return targetElement;
    }

    const formFieldOverlayElement = this.getTutorialFormFieldOverlayElement(targetElement);
    if (formFieldOverlayElement) {
      return formFieldOverlayElement;
    }

    const buttonLikeElement = this.getTutorialButtonLikeElement(targetElement);
    if (buttonLikeElement) {
      return buttonLikeElement;
    }

    return targetElement;
  }

  private getTutorialDialogSurfaceElement(targetElement: HTMLElement): HTMLElement | null {
    if (!targetElement.matches('[data-tutorial-target="role-help-dialog"]')) {
      return null;
    }

    return targetElement.closest('.mat-mdc-dialog-surface') as HTMLElement | null;
  }

  private getTutorialFormFieldOverlayElement(targetElement: HTMLElement): HTMLElement | null {
    const hasTutorialLabel = !!targetElement.querySelector('.bl-mat-label, .sidenav-header, .permission-info-text');
    if (hasTutorialLabel) {
      return null;
    }

    const targetIsInputLike = targetElement.matches('input, textarea, .mat-mdc-input-element, .mat-mdc-form-field, mat-form-field, .tutorial-input-shell, .bl-input');
    const closestFormField = targetElement.closest('.mat-mdc-form-field') as HTMLElement | null;
    const overlayElement = (targetElement.querySelector('.mat-mdc-form-field-focus-overlay, .mat-mdc-text-field-wrapper')
      || closestFormField?.querySelector('.mat-mdc-form-field-focus-overlay, .mat-mdc-text-field-wrapper')) as HTMLElement | null;

    if (!overlayElement) {
      return null;
    }

    return targetIsInputLike ? overlayElement : null;
  }

  private getTutorialButtonLikeElement(targetElement: HTMLElement): HTMLElement | null {
    if (this.isTutorialButtonLikeElement(targetElement)) {
      return targetElement;
    }

    if (targetElement.querySelector('.bl-mat-label, .sidenav-header, .permission-info-text')) {
      return null;
    }

    return targetElement.querySelector('button, .circle-button, .members-role-guide-button, .page-header-icon, .pen, .hover-icon') as HTMLElement | null;
  }

  private isTutorialButtonLikeElement(targetElement: HTMLElement): boolean {
    return targetElement.matches('button, .circle-button, .members-role-guide-button, .page-header-icon, .pen, .hover-icon');
  }

  private getTutorialTargetPadding(targetElement: HTMLElement | null | undefined): number {
    if (!targetElement) {
      return 10;
    }

    if (targetElement.closest('.role-help-dialog-panel')) {
      return 0;
    }

    if (targetElement.matches('.mat-mdc-form-field-focus-overlay, .mat-mdc-text-field-wrapper')) {
      return 4;
    }

    if (this.isTutorialButtonLikeElement(targetElement)) {
      return 4;
    }

    return 10;
  }

  private getTutorialTargetBorderRadius(targetElement: HTMLElement | null | undefined): string {
    if (!targetElement) {
      return '18px';
    }

    const borderRadius = window.getComputedStyle(targetElement).borderRadius;
    return borderRadius && borderRadius !== '0px' ? borderRadius : '18px';
  }

  private getTutorialTargetBorderRadiusValues(borderRadius: string, width: number, height: number): { topLeft: number; topRight: number; bottomRight: number; bottomLeft: number } {
    const radiusPart = (borderRadius || '18px').trim().split('/')[0]?.trim() || '18px';
    const tokens = radiusPart.split(/\s+/).filter(Boolean);
    const expandedTokens = this.expandTutorialBorderRadiusTokens(tokens);

    return {
      topLeft: this.resolveTutorialBorderRadiusToken(expandedTokens[0], width, height),
      topRight: this.resolveTutorialBorderRadiusToken(expandedTokens[1], width, height),
      bottomRight: this.resolveTutorialBorderRadiusToken(expandedTokens[2], width, height),
      bottomLeft: this.resolveTutorialBorderRadiusToken(expandedTokens[3], width, height),
    };
  }

  private expandTutorialBorderRadiusTokens(tokens: string[]): [string, string, string, string] {
    if (tokens.length === 0) {
      return ['18px', '18px', '18px', '18px'];
    }

    if (tokens.length === 1) {
      return [tokens[0], tokens[0], tokens[0], tokens[0]];
    }

    if (tokens.length === 2) {
      return [tokens[0], tokens[1], tokens[0], tokens[1]];
    }

    if (tokens.length === 3) {
      return [tokens[0], tokens[1], tokens[2], tokens[1]];
    }

    return [tokens[0], tokens[1], tokens[2], tokens[3]];
  }

  private resolveTutorialBorderRadiusToken(token: string | undefined, width: number, height: number): number {
    const normalizedToken = (token || '18px').trim();
    const rawRadius = normalizedToken.endsWith('%')
      ? Math.min(width, height) * (parseFloat(normalizedToken) / 100)
      : parseFloat(normalizedToken);
    const fallbackRadius = 18;
    const resolvedRadius = Number.isFinite(rawRadius) ? rawRadius : fallbackRadius;

    return Math.max(0, Math.min(resolvedRadius, width / 2, height / 2));
  }

  private buildTutorialShadePath(left: number, top: number, width: number, height: number, radii: { topLeft: number; topRight: number; bottomRight: number; bottomLeft: number }): string {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const right = left + width;
    const bottom = top + height;
    const outerPath = `M0 0 H${screenWidth} V${screenHeight} H0 Z`;
    const topLeft = radii.topLeft;
    const topRight = radii.topRight;
    const bottomRight = radii.bottomRight;
    const bottomLeft = radii.bottomLeft;

    if (topLeft <= 0 && topRight <= 0 && bottomRight <= 0 && bottomLeft <= 0) {
      return `${outerPath} M${left} ${top} H${right} V${bottom} H${left} Z`;
    }

    return `${outerPath} M${left + topLeft} ${top}`
      + ` H${right - topRight}`
      + (topRight > 0 ? ` A${topRight} ${topRight} 0 0 1 ${right} ${top + topRight}` : ` L${right} ${top}`)
      + ` V${bottom - bottomRight}`
      + (bottomRight > 0 ? ` A${bottomRight} ${bottomRight} 0 0 1 ${right - bottomRight} ${bottom}` : ` L${right} ${bottom}`)
      + ` H${left + bottomLeft}`
      + (bottomLeft > 0 ? ` A${bottomLeft} ${bottomLeft} 0 0 1 ${left} ${bottom - bottomLeft}` : ` L${left} ${bottom}`)
      + ` V${top + topLeft}`
      + (topLeft > 0 ? ` A${topLeft} ${topLeft} 0 0 1 ${left + topLeft} ${top}` : ` L${left} ${top}`)
      + ' Z';
  }

  private getExpandedTutorialSelectTrigger(targetElement: HTMLElement): HTMLElement | null {
    const expandedSelectSelector = '[role="combobox"][aria-expanded="true"][aria-controls], [role="listbox"][aria-expanded="true"][aria-controls], .mat-mdc-select[aria-expanded="true"][aria-controls], .mat-mdc-select-trigger[aria-expanded="true"][aria-controls]';

    if (targetElement.matches(expandedSelectSelector)) {
      return targetElement;
    }

    return targetElement.querySelector(expandedSelectSelector) as HTMLElement | null;
  }

  private getCombinedTutorialTargetRect(targetElements: HTMLElement[]): { top: number; left: number; right: number; bottom: number; width: number; height: number } | null {
    let combinedRect: { top: number; left: number; right: number; bottom: number; width: number; height: number } | null = null;

    for (const targetElement of targetElements) {
      const rect = this.getVisibleTutorialTargetRect(targetElement);
      if (!rect || !rect.width || !rect.height) {
        continue;
      }

      if (!combinedRect) {
        combinedRect = { ...rect };
        continue;
      }

      combinedRect = {
        top: Math.min(combinedRect.top, rect.top),
        left: Math.min(combinedRect.left, rect.left),
        right: Math.max(combinedRect.right, rect.right),
        bottom: Math.max(combinedRect.bottom, rect.bottom),
        width: 0,
        height: 0,
      };

      combinedRect.width = Math.max(0, combinedRect.right - combinedRect.left);
      combinedRect.height = Math.max(0, combinedRect.bottom - combinedRect.top);
    }

    return combinedRect;
  }

  private tryScrollTutorialTargetIntoView(targetSelector: string, targetElement: HTMLElement): boolean {
    if (targetElement.closest('.role-help-dialog-panel')) {
      return false;
    }

    if (this.tutorialAutoScrolledTargetSelector === targetSelector) {
      return false;
    }

    this.tutorialAutoScrolledTargetSelector = targetSelector;

    if (this.shouldScrollComponentGuideDataTypeOnMobile(targetElement)) {
      const componentGuideScrollParent = this.getComponentGuideTutorialScrollParent(targetElement);
      if (!componentGuideScrollParent) {
        return false;
      }

      componentGuideScrollParent.scrollTo({ top: 80, left: 0, behavior: 'smooth' });
      return true;
    }

    if (this.shouldScrollComponentGuideWorkspaceToBottomOnMobile(targetElement)) {
      const componentGuideScrollParent = this.getComponentGuideTutorialScrollParent(targetElement);
      if (!componentGuideScrollParent) {
        return false;
      }

      componentGuideScrollParent.scrollTo({
        top: Math.max(0, componentGuideScrollParent.scrollHeight - componentGuideScrollParent.clientHeight),
        left: 0,
        behavior: 'smooth',
      });
      return true;
    }

    if (this.shouldUseNativeTutorialScrollIntoView(targetElement)) {
      targetElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      return true;
    }

    const scrollParent = this.getNearestTutorialScrollParent(targetElement);
    const isInsideCdkOverlay = !!targetElement.closest('.cdk-overlay-container');

    if (scrollParent) {
      this.scrollTutorialTargetWithinParent(targetElement, scrollParent);
      return true;
    }

    if (isInsideCdkOverlay) {
      return false;
    }

    targetElement.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }

  private shouldScrollTutorialTargetIntoView(targetElement: HTMLElement): boolean {
    if (targetElement.closest('.role-help-dialog-panel')) {
      return false;
    }

    if (this.shouldScrollComponentGuideDataTypeOnMobile(targetElement)) {
      const componentGuideScrollParent = this.getComponentGuideTutorialScrollParent(targetElement);
      if (!componentGuideScrollParent) {
        return false;
      }

      return componentGuideScrollParent.scrollTop < 78;
    }

    if (this.shouldScrollComponentGuideWorkspaceToBottomOnMobile(targetElement)) {
      const componentGuideScrollParent = this.getComponentGuideTutorialScrollParent(targetElement);
      if (!componentGuideScrollParent) {
        return false;
      }

      const maxScrollTop = Math.max(0, componentGuideScrollParent.scrollHeight - componentGuideScrollParent.clientHeight);
      return componentGuideScrollParent.scrollTop < maxScrollTop - 2 || componentGuideScrollParent.scrollLeft !== 0;
    }

    if (this.shouldUseNativeTutorialScrollIntoView(targetElement)) {
      const targetRect = targetElement.getBoundingClientRect();
      const verticalPadding = 24;
      if (this.isTutorialTargetTooTallForViewport(targetRect.height, window.innerHeight, verticalPadding)) {
        return targetRect.top < verticalPadding || targetRect.top > window.innerHeight - verticalPadding;
      }

      return targetRect.top < verticalPadding || targetRect.bottom > window.innerHeight - verticalPadding;
    }

    const scrollParent = this.getNearestTutorialScrollParent(targetElement);
    const verticalPadding = 16;

    if (scrollParent) {
      const targetRect = targetElement.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();
      if (this.isTutorialTargetTooTallForViewport(targetRect.height, parentRect.height, verticalPadding)) {
        return targetRect.top < parentRect.top + verticalPadding || targetRect.top > parentRect.bottom - verticalPadding;
      }

      return targetRect.top < parentRect.top + verticalPadding || targetRect.bottom > parentRect.bottom - verticalPadding;
    }

    if (!!targetElement.closest('.cdk-overlay-container')) {
      return false;
    }

    const targetRect = targetElement.getBoundingClientRect();
    if (this.isTutorialTargetTooTallForViewport(targetRect.height, window.innerHeight, verticalPadding)) {
      return targetRect.top < verticalPadding || targetRect.top > window.innerHeight - verticalPadding;
    }

    return targetRect.top < verticalPadding || targetRect.bottom > window.innerHeight - verticalPadding;
  }

  private isTutorialTargetTooTallForViewport(targetSize: number, viewportSize: number, padding: number): boolean {
    return targetSize > Math.max(0, viewportSize - (padding * 2));
  }

  private shouldUseNativeTutorialScrollIntoView(targetElement: HTMLElement): boolean {
    return window.innerWidth <= 768
      && this.tutorialService.isComponentGuideTutorialActive
      && !this.shouldScrollComponentGuideWorkspaceToBottomOnMobile(targetElement)
      && !targetElement.closest('.cdk-overlay-container');
  }

  private shouldHideTutorialHighlightDuringAutoScroll(targetElement: HTMLElement): boolean {
    return window.innerWidth <= 768
      && this.tutorialService.isComponentGuideTutorialActive
      && !targetElement.closest('.cdk-overlay-container');
  }

  private shouldScrollComponentGuideWorkspaceToBottomOnMobile(targetElement: HTMLElement): boolean {
    if (window.innerWidth > 768 || !this.tutorialService.isComponentGuideTutorialActive || !!targetElement.closest('.cdk-overlay-container')) {
      return false;
    }

    switch (this.tutorialService.activeStepId) {
      case 'component-guide-value-triggers':
      case 'component-guide-value-trigger-add':
      case 'component-guide-value-trigger-condition':
      case 'component-guide-value-trigger-effect-add':
      case 'component-guide-value-trigger-effect-type':
      case 'component-guide-value-trigger-effect-change-value':
      case 'component-guide-value-trigger-effect-push':
      case 'component-guide-schedules':
      case 'component-guide-schedule-add':
      case 'component-guide-schedule-frequency':
      case 'component-guide-schedule-next-date':
      case 'component-guide-schedule-weeks':
      case 'component-guide-schedule-months':
      case 'component-guide-schedule-effects':
      case 'component-guide-finish':
        return true;
      default:
        return false;
    }
  }

  private shouldScrollComponentGuideDataTypeOnMobile(targetElement: HTMLElement): boolean {
    return window.innerWidth <= 768
      && this.tutorialService.isComponentGuideTutorialActive
      && this.tutorialService.activeStepId === 'component-guide-data-type'
      && !targetElement.closest('.cdk-overlay-container');
  }

  private getComponentGuideTutorialScrollParent(targetElement: HTMLElement): HTMLElement | null {
    const explicitScrollParent = targetElement.closest('.page-content-container') as HTMLElement | null;
    if (explicitScrollParent && explicitScrollParent.scrollHeight > explicitScrollParent.clientHeight + 1) {
      return explicitScrollParent;
    }

    return this.getNearestTutorialScrollParent(targetElement);
  }

  private getNearestTutorialScrollParent(targetElement: HTMLElement): HTMLElement | null {
    let ancestor = targetElement.parentElement;

    while (ancestor) {
      const style = window.getComputedStyle(ancestor);
      const clipsY = this.isTutorialOverflowClipping(style.overflowY);
      const clipsX = this.isTutorialOverflowClipping(style.overflowX);
      const canScrollY = ancestor.scrollHeight > ancestor.clientHeight + 1;
      const canScrollX = ancestor.scrollWidth > ancestor.clientWidth + 1;

      if ((clipsY && canScrollY) || (clipsX && canScrollX)) {
        return ancestor;
      }

      ancestor = ancestor.parentElement;
    }

    return null;
  }

  private scrollTutorialTargetWithinParent(targetElement: HTMLElement, scrollParent: HTMLElement) {
    const targetRect = targetElement.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(scrollParent);
    const verticalPadding = 16;
    const horizontalPadding = 16;
    const canScrollY = scrollParent.scrollHeight > scrollParent.clientHeight + 1
      && (parentStyle.overflowY === 'auto' || parentStyle.overflowY === 'scroll');
    const canScrollX = scrollParent.scrollWidth > scrollParent.clientWidth + 1
      && (parentStyle.overflowX === 'auto' || parentStyle.overflowX === 'scroll');
    let nextScrollTop = scrollParent.scrollTop;
    let nextScrollLeft = scrollParent.scrollLeft;

    if (canScrollY) {
      const tallTargetForParent = this.isTutorialTargetTooTallForViewport(targetRect.height, parentRect.height, verticalPadding);

      if (targetRect.top < parentRect.top + verticalPadding) {
        nextScrollTop -= (parentRect.top + verticalPadding) - targetRect.top;
      } else if (tallTargetForParent) {
        if (targetRect.top > parentRect.bottom - verticalPadding) {
          nextScrollTop += targetRect.top - (parentRect.top + verticalPadding);
        }
      } else if (targetRect.bottom > parentRect.bottom - verticalPadding) {
        nextScrollTop += targetRect.bottom - (parentRect.bottom - verticalPadding);
      }
    }

    if (canScrollX) {
      if (targetRect.left < parentRect.left + horizontalPadding) {
        nextScrollLeft -= (parentRect.left + horizontalPadding) - targetRect.left;
      } else if (targetRect.right > parentRect.right - horizontalPadding) {
        nextScrollLeft += targetRect.right - (parentRect.right - horizontalPadding);
      }
    } else if (nextScrollLeft !== 0) {
      nextScrollLeft = 0;
    }

    if (nextScrollTop !== scrollParent.scrollTop || nextScrollLeft !== scrollParent.scrollLeft) {
      scrollParent.scrollTo({
        top: nextScrollTop,
        left: nextScrollLeft,
        behavior: 'smooth',
      });
    }
  }

  private getVisibleTutorialTargetRect(targetElement: HTMLElement): { top: number; left: number; right: number; bottom: number; width: number; height: number } | null {
    const targetRect = targetElement.getBoundingClientRect();
    let visibleRect = {
      top: targetRect.top,
      left: targetRect.left,
      right: targetRect.right,
      bottom: targetRect.bottom,
      width: targetRect.width,
      height: targetRect.height,
    };

    let ancestor = targetElement.parentElement;

    while (ancestor) {
      const style = window.getComputedStyle(ancestor);
      const clipsX = this.isTutorialOverflowClipping(style.overflowX);
      const clipsY = this.isTutorialOverflowClipping(style.overflowY);

      if (clipsX || clipsY) {
        const ancestorRect = ancestor.getBoundingClientRect();
        visibleRect = {
          top: clipsY ? Math.max(visibleRect.top, ancestorRect.top) : visibleRect.top,
          left: clipsX ? Math.max(visibleRect.left, ancestorRect.left) : visibleRect.left,
          right: clipsX ? Math.min(visibleRect.right, ancestorRect.right) : visibleRect.right,
          bottom: clipsY ? Math.min(visibleRect.bottom, ancestorRect.bottom) : visibleRect.bottom,
          width: 0,
          height: 0,
        };

        visibleRect.width = Math.max(0, visibleRect.right - visibleRect.left);
        visibleRect.height = Math.max(0, visibleRect.bottom - visibleRect.top);

        if (!visibleRect.width || !visibleRect.height) {
          return null;
        }
      }

      ancestor = ancestor.parentElement;
    }

    return visibleRect;
  }

  private isTutorialOverflowClipping(overflowValue: string | null | undefined): boolean {
    return overflowValue === 'hidden' || overflowValue === 'clip' || overflowValue === 'auto' || overflowValue === 'scroll';
  }

  private setTutorialShadeToFullscreen() {
    this.tutorialHasTarget = false;
    this.tutorialSpotlightStyle = {};
    this.tutorialSpotlightCutoutStyle = {};
    this.tutorialShadeFill = this.tutorialService.activeStepDetails?.showShade === false
      ? 'transparent'
      : 'rgba(5, 8, 18, 0.7)';
    this.tutorialShadePath = `M0 0 H${window.innerWidth} V${window.innerHeight} H0 Z`;
    this.tutorialShadeStyles = [];
    this.tutorialSheetAtTop = this.shouldPlaceTutorialSheetAtTop(null);
  }

  private clearTutorialOverlay() {
    this.tutorialAutoScrolledTargetSelector = null;
    this.tutorialHasTarget = false;
    this.tutorialSpotlightStyle = {};
    this.tutorialSpotlightCutoutStyle = {};
    this.tutorialShadePath = '';
    this.tutorialShadeFill = 'rgba(5, 8, 18, 0.7)';
    this.tutorialShadeStyles = [];
    this.tutorialSheetAtTop = false;
  }

  ngAfterViewInit() {
    //this.startSplashWithOptionalDelay(this.minimumSplashScreenTime)

    this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {

      const currentUrl = event.urlAfterRedirects;
      this.hideLogoContainer = false;

    });
  }

  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  async appStateChanged() {
    if (!Capacitor.isNativePlatform()) return;

    this.ngZone.run(async () => {
      if (!this.userAuthService.loggedIn) return;

      if (!this.liveUpdatesService.isConnected()) {
        // Socket is disconnected — data is guaranteed stale. Reload is the only reliable fix.
        console.warn('[App] Socket disconnected on resume. Reloading...');
        window.location.reload();
      } else {
        // Socket stayed connected or auto-reconnected — do a silent data refresh
        console.log('[App] Socket connected on resume. Silent refresh...');
        await this.silentRefresh();
      }
    });
  }

  private async silentRefresh(): Promise<void> {
    if (!this.userAuthService.loggedIn) return;
    if (this.isRefreshing) return;

    const THROTTLE_MS = 5_000; // Prevent double-calls; getUserCompleteFromServer fetches all state
    if (Date.now() - this.lastSilentRefreshTime < THROTTLE_MS) return;

    this.isRefreshing = true;
    this.lastSilentRefreshTime = Date.now();
    console.log('[App] Silent refresh started');

    await this.ngZone.run(async () => {
      try {
        await this.userAuthService.getUserCompleteFromServer(true); // force-refresh token — may have expired during background

        // Re-run notification setup — same loop as initial login, required for proper state
        try {
          const notifications = this.userAuthService.currentUserComplete?.userNotificationsComplete?.userNotifications ?? [];
          for (const notification of notifications) {
            await this.userNotificationService.setupUserNotificationLocally(notification);
          }
        } catch (notifErr) {
          console.error('[App] Notification setup failed during refresh (non-fatal):', notifErr);
        }

        if (!this.liveUpdatesService.isConnected()) {
          await this.setupSocketConnection();
        } else {
          // Socket already connected — rejoin rooms with fresh lab list since the
          // connect-time joinAllUserLabRooms fired before data was loaded
          this.liveUpdatesService.joinAllUserLabRooms(this.userAuthService);
        }

        // Re-point currentSelected* to fresh objects from the new snapshot so
        // nothing holds a reference into the old (now orphaned) object tree
        this.labService.refreshSelectedReferences(this.userAuthService);

        // Signal graph components and any other consumers to re-fetch their own data
        this.labService.dataRefreshed$.next();

        this.reloadedApp$.next();
        console.log('[App] Silent refresh complete');
      } catch (err) {
        console.error('[App] Silent refresh failed:', err);
      } finally {
        this.isRefreshing = false;
      }
    });
  }


  startSplashWithOptionalDelay(minSplashScreenTime: number) {
    // Always clear any existing fallback timeout before showing splash
    if (this.splashFallbackTimeoutId) {
      clearTimeout(this.splashFallbackTimeoutId);
      this.splashFallbackTimeoutId = null;
    }
    console.log('startSplashWithOptionalDelay called', { isResumeSplash: this.isResumeSplash, showSplash: this.showSplash, hideApp: this.hideApp });
    this.hideLogoContainer = false;
    if (this.showSplash) {
      return;
    }
    setTimeout(() => {
      this.showSplash = true;
      this.splashFadeOut = false;
      this.hasReachedMinimumTime = false;
      this.hideApp = true;
      this.splashFadeOutTriggered = false; // Reset flag when starting new splash
      console.log('Splash shown', { showSplash: this.showSplash, splashFadeOut: this.splashFadeOut, hideApp: this.hideApp });

      //If we get here, it means we loaded the app for the first time
      // this.soundService.playSound(this.splashSoundSrc,1);
      setTimeout(async () => {
        this.finishedMinimumSplashScreenTime = true;
        if (this.disableAfterMinimumTime) {
          this.finishIntro();
        }
      }, minSplashScreenTime);

    }, 200)
  }

  finishIntro() {
    // Prevent multiple calls to finishIntro
    if (this.splashFadeOutTriggered) {
      console.log('finishIntro called but splash fade out already triggered, ignoring');
      return;
    }

    console.log("finishIntro called", { hasReachedMinimumTime: this.hasReachedMinimumTime, showSplash: this.showSplash, splashFadeOut: this.splashFadeOut, hideApp: this.hideApp });
    // Clear fallback timeout to prevent double fade out
    if (this.splashFallbackTimeoutId) {
      clearTimeout(this.splashFallbackTimeoutId);
      this.splashFallbackTimeoutId = null;
    }
    this.hasReachedMinimumTime = true;
    this.hideApp = false;
    console.log('hideApp set to', this.hideApp);
    this.firstAppLoad = false;
    if (this.userAuthService.didLoginCheck == true) {
      console.log('Splash reached minimum time, hiding splash', this.userAuthService.loggedIn, this.userAuthService.currentUserComplete);
      this.triggerFadeOutSplash();
    }
  }

  triggerFadeOutSplash() {
    // Prevent multiple fade out triggers
    if (this.splashFadeOutTriggered) {
      console.log('triggerFadeOutSplash called but already triggered, ignoring');
      return;
    }

    console.log('triggerFadeOutSplash called', { showSplash: this.showSplash, splashFadeOut: this.splashFadeOut, hideApp: this.hideApp });
    this.splashFadeOutTriggered = true; // Set flag to prevent multiple triggers
    this.splashFadeOut = true;

    setTimeout(() => {
      this.showSplash = false;
      this.splashFadeOut = false; // Reset here
      this.isResumeSplash = false; // Reset resume splash flag after fade out
      // Clear fallback timeout to prevent double fade out
      if (this.splashFallbackTimeoutId) {
        clearTimeout(this.splashFallbackTimeoutId);
        this.splashFallbackTimeoutId = null;
      }
      console.log('Splash fully hidden', { showSplash: this.showSplash, splashFadeOut: this.splashFadeOut, hideApp: this.hideApp });
    }, 1000);
  }

  /*
  async loadSplashScreen() {
    // On iOS cold start, let the native splash screen handle itself
    // Don't interfere with the native splash screen behavior
    if (Capacitor.isNativePlatform() && this.firstAppLoad) {
      // Just mark as loaded and let the native splash continue naturally
      this.loadedSplashScreen = true;
      
      setTimeout(async () => {
        this.finishedMinimumSplashScreenTime = true;
        if (this.disableAfterMinimumTime) {
          await this.hideSplashScreen();
        }
      }, this.minimumSplashScreenTime);
    } else {
      // For subsequent loads or web, use the normal hide/show cycle
      await SplashScreen.hide();
      await SplashScreen.show({
        autoHide: false,
      });

      this.loadedSplashScreen = true;

      setTimeout(async () => {
        this.finishedMinimumSplashScreenTime = true;
        if (this.disableAfterMinimumTime) {
          await this.hideSplashScreen();
        }
      }, this.minimumSplashScreenTime);
    }
  }*/

  async hideSplashScreen() {
    //await SplashScreen.hide();
  }

  /**
   * Preloads the splash image for instant display
   */
  preloadSplashImage() {
    const img = new Image();
    img.onload = () => {
      console.log('Splash image loaded');
      this.imageLoaded = true;
    };
    if (window.innerWidth < smallScreenSize) {
      img.src = this.splashImageSrcMobile;
    }
    else {
      img.src = this.splashImageSrc;
    }

    // Handle case where image is already cached
    if (img.complete) {
      console.log('Splash image was already cached');
      this.imageLoaded = true;
    }
  }

  /**
   * Called when the splash image has loaded
   */
  onImageLoaded() {
    setTimeout(() => {
      this.imageLoaded = true;
    }, 0);
  }

  /**
   * Sets a CSS variable to ensure content respects actual viewport height
   */
  setRealViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }


  setupPushNotifications() {
    if (Capacitor.isNativePlatform()) {
      try {
        PushNotifications.requestPermissions().then(result => {
          if (result.receive === 'granted') {
            PushNotifications.register(); // 👈 THIS IS REQUIRED
          } else {
            console.warn('Push permission not granted');
          }
        });

        PushNotifications.addListener('registration', async (token) => {
          console.log('FCM Token:', token.value);

          this.pushNotificationService.registerPushNotificationUser(token.value, this.userAuthService);
        });
      }
      catch (error) {
        console.log('Error setting up push notifications:', error);
      }
    }
  }



  private setupSocketEventListeners() {
    console.log('[App] Setting up socket event listeners');
    console.log('[App] Socket status:', {
      exists: !!this.liveUpdatesService.socket,
      connected: this.liveUpdatesService.socket?.connected,
      id: this.liveUpdatesService.socket?.id
    });

    // Clear any existing subscriptions first
    if (this.socketSubscriptions) {
      this.socketSubscriptions.unsubscribe();
    }
    this.socketSubscriptions = new Subscription();

    //LAB CONTROLLER UPDATES
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLivelabControllerCompleteUpdates().subscribe((labControllerComplete: LabControllerComplete) => {
        this.labService.updateLabControllerCompleteLocally(labControllerComplete, this.userAuthService);
      })
    );
    //LAB SETTINGS
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLabSettingsUpdates().subscribe(async (ioLabSettings: ioLabSettings) => {
        await this.labService.updateLabSettingsLocally(ioLabSettings, this.userAuthService);
      })
    );
    //LAB USERS UPDATED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLabUsersUpdates().subscribe((data: LabUsersUpdate) => {
        this.labService.upsertLabUsersCompleteLocally(data.labUsersComplete, this.userAuthService, this.gadgetService);
        if (data.gadgetUsersComplete && data.gadgetUsersComplete.length > 0) {
          this.gadgetService.upsertGadgetUsersCompleteLocally(data.gadgetUsersComplete, this.userAuthService);
        }
      })
    );

    //LAB USERS DELETED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLabUsersDeleted().subscribe((data: { labId: string, userIds: string[] }) => {
        console.log("LAB USERS DELETED", data);
        this.labService.deleteLabUsersLocally(data.labId, data.userIds, this.userAuthService);
      })
    );
    //LAB USER CREATED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onCreatedLabUserUpdates().subscribe((labUserComplete: LabUserComplete) => {
        console.log("lab user created", labUserComplete)
        this.labService.createNewLabUserLocally(labUserComplete, this.userAuthService, this.gadgetService);

        // Remove any existing lab user requests for this user since they're now a lab member
        this.labService.removeLabUserRequestsForUserId(
          labUserComplete.labUser.labId,
          labUserComplete.labUser.userId,
          this.userAuthService
        );
      })
    );
    //NEW LAB CREATED (other tabs)
    this.socketSubscriptions.add(
      this.liveUpdatesService.onNewLabCompleteUpdates().subscribe((labComplete: LabComplete) => {
        console.log("[App] New lab received via socket:", labComplete.lab?.id);
        this.labService.gotNewLabComplete(labComplete, this.userAuthService, this.gadgetService);
      })
    );
    //LAB DELETED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onDeletedLabUpdates().subscribe((labId: string) => {
        console.log("[App] Lab deleted, leaving room for labId:", labId);
        // Leave the lab room since lab was deleted
        this.liveUpdatesService.leaveLabRoom(labId);
        this.labService.deleteLabLocally(this.userAuthService, labId);
      })
    );
    //GADGET SETTINGS
    this.socketSubscriptions.add(
      this.liveUpdatesService.onGadgetSettingsUpdates().subscribe(async (ioGadgetSettings: ioGadgetSettings) => {
        this.gadgetService.updateGadgetSettingsLocally(ioGadgetSettings, this.userAuthService);
      })
    );
    //GADGET DELETED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onDeletedGadgetUpdates().subscribe((gadgetId: string) => {
        this.gadgetService.deleteGadgetLocally(gadgetId, this.userAuthService);
      })
    );
    //COMPONENT DELETED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onDeletedComponentUpdates().subscribe((componentId: string) => {
        this.gadgetService.deleteComponentLocally(componentId, this.userAuthService);
      })
    );
    //GADGET USERS UPDATED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onGadgetUsersUpdates().subscribe((data: any) => {
        if (data.gadgetUsersComplete) {
          this.gadgetService.upsertGadgetUsersCompleteLocally(data.gadgetUsersComplete, this.userAuthService);
        }
      })
    );

    //LAB USER REQUEST DELETED
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLabUserRequestDeleted().subscribe((data: { labUserRequestId: string, labId: string }) => {
        console.log("[App] Received LabUserRequestDeleted event:", data);
        console.log("[App] Calling labService.removeLabUserRequestLocally with:", data);
        this.labService.removeLabUserRequestLocally(data.labId, data.labUserRequestId, this.userAuthService);
        console.log("[App] removeLabUserRequestLocally completed");
      })
    );

    //LAB USER REMOVED (current user kicked from lab)
    this.socketSubscriptions.add(
      this.labService.currentSelectedLabUserRemoved.subscribe((data: { labId: string, message: string }) => {
        console.log("[App] Received currentSelectedLabUserRemoved event:", data);

        // Leave the lab room since user was removed/kicked
        this.liveUpdatesService.leaveLabRoom(data.labId);

        // Check if this is the currently selected lab
        if (this.labService.currentSelectedLab && this.labService.currentSelectedLab.lab.id === data.labId) {
          console.log("[App] Current user was removed from selected lab, redirecting to labs");

          // Clear the current selected lab
          this.labService.currentSelectedLab = null;

          // Navigate back to labs
          this.router.navigate(['/labs']);

          // Show snackbar message
          setTimeout(() => {
            presentSnackBar(data.message, SnackBarColor.DANGER, this.matSnackBar);
          }, 100);
        }
      })
    );
    //GADGET USERS CREATED FOR ALL LAB USERS
    this.socketSubscriptions.add(
      this.liveUpdatesService.onNewGadgetUsersUpdates().subscribe((updatedGadgetUsersComplete: GadgetUserComplete[]) => {
        this.gadgetService.addCreatedGadgetUsersLocally(updatedGadgetUsersComplete, this.userAuthService);
      })
    );
    //GADGET COMPLETE CREATED UPDATE
    this.socketSubscriptions.add(
      this.liveUpdatesService.onGadgetCompleteUpdates().subscribe((gadgetComplete: GadgetComplete) => {
        console.log('[App] Received GadgetComplete update via socket for gadget:', gadgetComplete.gadget.id);
        setTimeout(() => {
          this.gadgetService.createGadgetCompleteLocally(gadgetComplete, this.userAuthService);
        }, 100)
      })
    );
    //COMPONENT COMPLETE UPDATE
    this.socketSubscriptions.add(
      this.liveUpdatesService.onComponentSettingsUpdates().subscribe((componentSettingsReturn: ComponentSettingsReturn) => {
        console.log("component settings updated")
        this.labService.updateComponentSettingsLocally(componentSettingsReturn, this.userAuthService, true);
      })
    );
    //COMPONENT VALUE UPDATE
    this.socketSubscriptions.add(
      this.liveUpdatesService.onComponentValueUpdates().subscribe((component: Component) => {
        this.labService.updateComponentValueLocally(component, this.userAuthService)
      })
    );
    //COMPONENT SCHEDULE UPDATE
    this.socketSubscriptions.add(
      this.liveUpdatesService.onComponentScheduleNextDateUpdates().subscribe((componentSchedules: ComponentSchedule[]) => {
        this.labService.updateComponentSchedulesLocally(componentSchedules, this.userAuthService)
      })
    );
    //CONTACT UPDATES
    this.socketSubscriptions.add(
      this.liveUpdatesService.onContactUpdates().subscribe((userContact: UserContact) => {
        console.log("UPDATED SOCKET CONTACT", userContact)

        // Handle deleted contacts - remove from list if status is DELETED
        if (userContact.status == UserContactStatus.DELETED) {
          // Find and remove the contact by contactUserId (since id might be null for deleted contacts)
          for (let i = this.userAuthService.currentUserComplete.userContacts.length - 1; i >= 0; i--) {
            let currentUserContact = this.userAuthService.currentUserComplete.userContacts[i];
            if (currentUserContact.contactUserId == userContact.contactUserId ||
              (currentUserContact.id == userContact.id && userContact.id != null)) {
              console.log("REMOVING DELETED CONTACT", currentUserContact);
              this.userAuthService.currentUserComplete.userContacts.splice(i, 1);
              this.liveUpdatesService.triggerLocalContactUpdates(userContact);
              return;
            }
          }
          return;
        }

        let foundContact = false;
        //Loop through contacts
        for (let i = 0; i < this.userAuthService.currentUserComplete.userContacts.length; i++) {
          let currentUserContact = this.userAuthService.currentUserComplete.userContacts[i];
          //If contact already exists in client
          if (currentUserContact.id == userContact.id ||
            (currentUserContact.contactUserId == userContact.contactUserId && userContact.id == null)) {
            updateObjKeepingRef(currentUserContact, userContact)
            foundContact = true;
          }
        }

        if (foundContact == false) {
          console.log("ADDING CONTACT")
          this.userAuthService.currentUserComplete.userContacts.push(userContact);
        }
        this.liveUpdatesService.triggerLocalContactUpdates(userContact);
      })
    );
    //USER NOTIFICATION UPDATES
    this.socketSubscriptions.add(
      this.liveUpdatesService.onLiveUserNotificationUpdates().subscribe(async (updatedUserNotification: UserNotification) => {
        console.log("got user notification", updatedUserNotification);
        let foundUserNotification = false;
        await this.userNotificationService.setupUserNotificationLocally(updatedUserNotification);
        for (let i = 0; i < this.userAuthService.currentUserComplete.userNotificationsComplete.userNotifications.length; i++) {
          let userNotification = this.userAuthService.currentUserComplete.userNotificationsComplete.userNotifications[i];
          if (userNotification.id == updatedUserNotification.id) {
            updateObjKeepingRef(userNotification, updatedUserNotification)
            foundUserNotification = true;
            console.log("found notification and updated")
            break;
          }
        }
        if (!foundUserNotification) {
          this.userAuthService.currentUserComplete.userNotificationsComplete.unreadNotifications++;
          this.userAuthService.currentUserComplete.userNotificationsComplete.userNotifications.unshift(updatedUserNotification);
        }
      })
    );
  }
}